/**
 * Line path strategy abstraction (PRD-F5 §TR3).
 *
 * The line slice exposes a pluggable path computation contract so future
 * strategies (Bezier, Orthogonal, …) can be added without editing the
 * renderer. P5.5 ships only `straight` (TR4) — additional strategies are
 * out-of-scope per PRD §Failure Conditions FC13.
 */

/**
 * Konva.Line props produced by a strategy. Kept structurally compatible
 * with `Konva.LineConfig` but only the fields the renderer reads are
 * declared — keeps the contract tight and Konva-version-independent.
 */
export interface KonvaLineProps {
  readonly points: readonly number[];
  readonly tension?: number;
  readonly bezier?: boolean;
}

/**
 * Which edge of a docked target an endpoint attaches to. `left`/`right` are
 * vertical edges (a segment touching them must leave horizontally); `top`/
 * `bottom` are horizontal edges (segment must leave vertically). Mirrors
 * `AnchorSide` from the board-object model but is redeclared here so the
 * strategy layer stays decoupled from the object interfaces.
 */
export type PathEndpointSide = 'top' | 'right' | 'bottom' | 'left';

/**
 * Optional routing hints for a strategy.
 *  - `startSide`/`endSide`: when an endpoint is docked to an element edge, the
 *    strategy must make the segment touching that endpoint perpendicular to the
 *    edge. `undefined` for a free (un-docked) endpoint.
 *  - `midRatio`: where the elbow's middle segment sits between the endpoints,
 *    as a fraction in [0,1] (the user drags it). `undefined` → 0.5 (midpoint).
 *    Free-form strategies (straight) ignore all of these.
 */
export interface PathEndpointHints {
  readonly startSide?: PathEndpointSide;
  readonly endSide?: PathEndpointSide;
  readonly midRatio?: number;
}

/**
 * A pluggable line path strategy. The `id` MUST be unique — registering a
 * strategy with an already-known id overwrites the previous registration.
 */
export interface LinePathStrategy {
  readonly id: string;
  /**
   * Compute Konva.Line props for the given start/end points.
   * `controlPoints` is optional for strategies that support curve editing
   * (Bezier, Spline, …). Straight ignores it.
   * `hints` carries the docked-edge sides so an orthogonal strategy can make
   * the touching segments perpendicular to the edge; free-form strategies
   * (straight) ignore it.
   */
  computePath(
    start: { x: number; y: number },
    end: { x: number; y: number },
    controlPoints?: readonly { x: number; y: number }[],
    hints?: PathEndpointHints,
  ): KonvaLineProps;
  /**
   * Optional: return default control points between start and end.
   * Used by future curve editors. Straight strategy omits this method.
   */
  getControlPoints?(
    start: { x: number; y: number },
    end: { x: number; y: number },
  ): readonly { x: number; y: number }[];
}

/**
 * Fallback strategy id used by `LinePathStrategyRegistry.get()` when the
 * requested id is `undefined` or unknown. Forward-compatibility: persisted
 * boards referencing strategies that aren't registered in the current
 * runtime still render as straight lines instead of throwing.
 */
export const FALLBACK_STRATEGY_ID = 'straight';

/**
 * Registry for `LinePathStrategy` instances (PRD-F5 §TR3).
 *
 * Lifecycle: a single registry instance is created in `firstUpdated` of
 * the whiteboard element and populated by `lineSlice.registerStrategies?`
 * (TR8b). The renderer reads via `registry.get(obj.pathStrategy)`.
 */
export class LinePathStrategyRegistry {
  private readonly strategies = new Map<string, LinePathStrategy>();

  /** Register a strategy. Overwrites if `strategy.id` already registered. */
  register(strategy: LinePathStrategy): void {
    this.strategies.set(strategy.id, strategy);
  }

  /**
   * Returns the strategy for the given id. Falls back to the strategy
   * registered under `FALLBACK_STRATEGY_ID` when the id is `undefined` or
   * unknown — this keeps persisted boards forward-compatible (FC11).
   *
   * Throws only if neither the requested id nor the fallback strategy is
   * registered (a setup bug — `lineSlice.registerStrategies` was never
   * invoked).
   */
  get(id: string | undefined): LinePathStrategy {
    if (id !== undefined) {
      const direct = this.strategies.get(id);
      if (direct !== undefined) return direct;
    }
    const fallback = this.strategies.get(FALLBACK_STRATEGY_ID);
    if (fallback === undefined) {
      throw new Error(
        `LinePathStrategyRegistry: no strategy registered for id="${id ?? 'undefined'}" and no fallback "${FALLBACK_STRATEGY_ID}" strategy available.`,
      );
    }
    return fallback;
  }

  /** Whether a strategy with the given id is registered. */
  has(id: string): boolean {
    return this.strategies.has(id);
  }
}
